<template>
  <div class="voice-playground-root ai-agent-setup relative flex flex-col overflow-x-hidden" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div
      class="title-row mt-6 ml-4 mr-4 m-2 flex flex-col pb-3"
      :class="{ 'rtl-row': isRtl === true }"
    >
      <div class="w-full" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
        <span class="badge badge-primary font-semibold uppercase tracking-wide">
          {{ t('voicePlayground.badge', 'Voice Playground') }}
        </span>
        <div class="mt-3 flex flex-col xl:flex-row xl:items-start xl:justify-between">
          <div class="min-w-0 flex-1">
            <h1 class="text-2xl font-medium text-gray-900 dark:text-gray-200">
              {{ t('voicePlayground.title', 'Make every sentence sound sellable') }}
            </h1>
            <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">
              {{ t('voicePlayground.writerSubtitle', 'Write a sentence, watch the direction adapt, then generate a paid voice preview.') }}
            </p>
          </div>
          <div class="voice-balance-card mt-3 w-full min-w-[220px] max-w-[280px] rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 xl:mt-0 xl:w-auto xl:flex-shrink-0">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-gray-900 dark:text-gray-200">{{ t('voicePlayground.balanceTitle', 'Voice seconds left') }}</span>
              <span class="badge badge-primary badge-sm font-bold tabular-nums">{{ remainingSecondsLabel }}</span>
            </div>
            <div class="mt-3 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div class="h-full rounded-full bg-primary transition-all duration-300" :style="balanceProgressStyle"></div>
            </div>
            <div class="mt-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
              <span>{{ t('voicePlayground.afterGenerate', 'After generate') }}</span>
              <span class="font-semibold tabular-nums text-gray-900 dark:text-gray-200">{{ estimatedAfterGenerateLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="voice-playground-card mt-2 mb-5 mx-5 min-w-0 flex flex-col overflow-x-hidden overflow-y-visible rounded-md border border-gray-200 bg-white p-0 dark:border-gray-700 dark:bg-gray-800">
      <div class="voice-playground-columns flex flex-col overflow-x-hidden overflow-y-visible xl:flex-row xl:items-stretch">
        <div class="voice-playground-editor flex min-w-0 flex-col overflow-x-hidden overflow-y-visible border-b border-gray-200 p-6 dark:border-gray-700 xl:w-[56%] xl:max-w-[56%] xl:flex-none xl:border-b-0 xl:border-r">
            <div class="voice-playground-editor-scroll flex flex-col pr-1 pb-2" :class="isRtl === true ? 'pl-1 pr-0' : ''">
              <div class="mb-4 flex-shrink-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
                <h2 class="text-xl font-medium text-gray-900 dark:text-gray-200">{{ t('voicePlayground.writerTitle', 'Sentence lab') }}</h2>
              </div>
              <div class="voice-playground-textarea-wrap relative mb-4 flex-shrink-0 flex flex-col">
                <textarea
                  v-model="playgroundText"
                  rows="10"
                  class="textarea textarea-bordered voice-playground-textarea voice-playground-field w-full min-h-[260px] max-h-[34rem] resize-y overflow-y-auto bg-white text-sm leading-relaxed dark:bg-gray-800"
                  :class="textDirection === 'rtl' ? 'text-right' : 'text-left'"
                  :dir="textDirection"
                  :placeholder="t('voicePlayground.placeholder', 'Type the sentence you want to hear...')"
                ></textarea>
                <div
                  class="mt-2 flex flex-wrap items-center justify-between text-xs text-gray-500 dark:text-gray-400"
                  :class="isRtl === true ? 'flex-row-reverse' : ''"
                >
                  <span class="tabular-nums">{{ wordCount }} {{ t('voicePlayground.words', 'words') }} · {{ characterCount }} {{ t('voicePlayground.characters', 'characters') }}</span>
                  <span v-if="lastGeneratedSeconds > 0" class="badge badge-success badge-sm font-semibold">
                    {{ t('voicePlayground.billedText', 'Billed seconds') }} {{ formatSeconds(lastGeneratedSeconds) }}
                  </span>
                </div>
              </div>

              <div v-if="generating === true || audioUrl !== ''" class="voice-audio-preview mb-4 flex-shrink-0">
                <div v-if="generating === true" class="voice-audio-loading">
                  <div class="voice-player-skeleton rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div class="mb-3 flex items-center justify-center" :class="isRtl === true ? 'flex-row-reverse' : ''">
                      <svg xmlns="http://www.w3.org/2000/svg" class="voice-audio-spinner h-6 w-6 flex-shrink-0 animate-spin text-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div class="skeleton mb-3 h-10 w-full rounded-md bg-gray-200 dark:bg-gray-700"></div>
                    <div class="flex items-center" :class="isRtl === true ? 'flex-row-reverse' : ''">
                      <div class="skeleton h-9 w-9 flex-shrink-0 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                      <div class="skeleton mx-3 h-2 min-w-0 flex-1 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                      <div class="skeleton h-7 w-14 flex-shrink-0 rounded-md bg-gray-200 dark:bg-gray-700"></div>
                    </div>
                  </div>
                </div>
                <audio v-else class="voice-audio-el w-full rounded-md" :src="audioUrl" controls preload="metadata"></audio>
              </div>

              <div class="voice-playground-examples mb-4 flex-shrink-0">
                <p
                  class="label mb-2 cursor-pointer text-[11px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                  :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
                >
                  {{ t('voicePlayground.exampleTitle', 'Examples') }}
                </p>
                <div class="voice-example-buttons flex flex-wrap">
                  <button
                    v-for="example in sentenceExamples"
                    :key="example"
                    type="button"
                    class="btn"
                    @click="setExampleText(example)"
                  >
                    {{ example }}
                  </button>
                </div>
              </div>

              <div v-if="replacementItems.length > 0" class="voice-playground-replacements mb-3 flex-shrink-0 rounded-md border border-dashed border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <p
                  class="label mb-2 cursor-pointer text-[11px] font-bold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                  :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
                >
                  {{ t('termsPackages.fineTuningTitle', 'Pronunciation List') }}
                </p>
                <div class="flex flex-wrap">
                  <span
                    v-for="entry in replacementItems"
                    :key="entry.term + entry.spokenAs"
                    class="badge mb-2 border border-base-300 bg-base-100 font-mono text-xs font-normal text-base-content"
                    :class="isRtl === true ? 'ml-2' : 'mr-2'"
                  >
                    {{ entry.term }} → {{ entry.spokenAs }}
                  </span>
                </div>
              </div>
            </div>

            <div
              class="voice-playground-actions mt-auto flex flex-shrink-0 flex-col border-t border-gray-200 pt-4 dark:border-gray-700"
            >
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-end">
                <button type="button" class="btn" @click="resetPlayground">
                  {{ t('voicePlayground.reset', 'Reset') }}
                </button>
                <button
                  type="button"
                  class="btn"
                  :disabled="isGenerateDisabled === true"
                  @click="generateVoice"
                >
                  <span v-if="generating === true" class="loading loading-spinner loading-sm" :class="isRtl === true ? 'ml-2' : 'mr-2'"></span>
                  {{ generateButtonLabel }}
                </button>
              </div>
              <p
                class="mb-0 mt-3 w-full flex-shrink-0 text-xs font-medium leading-relaxed sm:text-right"
                :class="hasEnoughEstimatedSeconds === true ? 'text-gray-500 dark:text-gray-400' : 'text-error'"
                :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
              >
                {{ generateHelpText }}
              </p>
            </div>
          </div>

          <div class="voice-playground-settings-panel flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-visible p-6">
            <div class="voice-settings-intro mb-4 flex-shrink-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
              <p class="text-xl font-medium text-gray-900 dark:text-gray-200">
                {{ t('voicePlayground.settingsTitle', 'Voice stack') }}
              </p>
              <p class="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {{ t('voicePlayground.settingsSubtitle', 'Choose the voice, language, word collection, and speed. Open Advanced for pacing, pauses, and tone.') }}
              </p>
            </div>
            <div class="voice-settings-scroll voice-playground-selectors pr-1 pb-2" :class="isRtl === true ? 'pl-1 pr-0' : ''">
              <VoiceSettingsFields
              :selectedVoiceId="selectedVoiceId"
              :voiceType="playgroundVoiceType"
              :accent="accent"
              :language="language"
              :languageOptions="languageOptions"
              :selectedTermsPackages="selectedTermsPackages"
              :speed="speed"
              :customPacingEnabled="customPacingEnabled"
              :pacing="pacing"
              :durationStretch="durationStretch"
              :useDashPauses="useDashPauses"
              :pauseEveryWords="pauseEveryWords"
              :dashPauseMs="dashPauseMs"
              :commaPauseMs="commaPauseMs"
              :periodPauseMs="periodPauseMs"
              :tone="tone"
              :toneOptions="toneOptions"
              :isRtl="isRtl === true"
              :collapseAdvanced="true"
              :hideModeAndCancelling="true"
              :hideProvider="true"
              :hideFillers="true"
              :hideAdvancedEngineSelectors="true"
              @update:selectedVoiceId="selectedVoiceId = $event"
              @update:accent="accent = $event"
              @update:language="language = $event"
              @update:selectedTermsPackages="selectedTermsPackages = $event"
              @update:selectedTerms="selectedTerms = $event"
              @update:speed="speed = $event"
              @update:customPacingEnabled="customPacingEnabled = $event"
              @update:pacing="pacing = $event"
              @update:durationStretch="durationStretch = $event"
              @update:useDashPauses="useDashPauses = $event"
              @update:pauseEveryWords="pauseEveryWords = $event"
              @update:dashPauseMs="dashPauseMs = $event"
              @update:commaPauseMs="commaPauseMs = $event"
              @update:periodPauseMs="periodPauseMs = $event"
              @update:tone="tone = $event"
              />
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import axiosInstance from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import VoiceSettingsFields from '@/components/VoiceSettingsFields.vue';
import { resolveVoiceDisplayName } from '@/utils/feminineVoiceDisplay.js';
import { geminiLanguageNames, geminiLanguageDetails } from '@/utils/geminiLanguages.js';
import { defaultVoiceId, fetchVoices, getVoiceOptionById, voiceOptions } from '@/utils/voiceCatalog';

const toast = useToast();
const { t, currentLanguage } = useI18n();

const playgroundText = ref('Your next enterprise voice agent should sound confident, fast and perfectly on brand.');
const selectedVoiceId = ref('');
const accent = ref('Israeli');
const language = ref('English');
const selectedTermsPackages = ref([]);
const selectedTerms = ref([]);
const speed = ref(1);
const customPacingEnabled = ref(true);
const pacing = ref(1.05);
const durationStretch = ref(1);
const useDashPauses = ref(true);
const pauseEveryWords = ref(0);
const dashPauseMs = ref(180);
const commaPauseMs = ref(120);
const periodPauseMs = ref(260);
const tone = ref('Neutral');
const mode = ref('interruptible');
const cancellingMode = ref('fast');
const generating = ref(false);
const voiceUsageLoading = ref(false);
const totalVoiceMinutes = ref(null);
const remainingVoiceMinutes = ref(null);
const lastGeneratedSeconds = ref(0);
const audioUrl = ref('');
const audioElement = ref(null);
const termsPackages = ref([]);

const languageOptions = computed(() => geminiLanguageNames);

const toneOptions = computed(() => [
  { id: 'Neutral', label: t('aiChatAgentSetup.voiceSettings.toneNeutral', 'Neutral') },
  { id: 'Warm', label: t('aiChatAgentSetup.voiceSettings.toneWarm', 'Warm') },
  { id: 'Confident', label: t('aiChatAgentSetup.voiceSettings.toneConfident', 'Confident') },
  { id: 'Calm', label: t('aiChatAgentSetup.voiceSettings.toneCalm', 'Calm') },
  { id: 'Energetic', label: t('aiChatAgentSetup.voiceSettings.toneEnergetic', 'Energetic') }
]);

const sentenceExamples = computed(() => [
  t('voicePlayground.example1', 'Welcome to your AI concierge. How can I help you today?'),
  t('voicePlayground.example2', 'Let me check your account and find the best option for your team.'),
  t('voicePlayground.example3', 'Your demo is ready. We can launch the voice agent this week.')
]);

const isRtl = computed(() => {
  return ['he', 'ar'].includes(currentLanguage.value) === true;
});

const normalizedText = computed(() => {
  return typeof playgroundText.value === 'string' ? playgroundText.value.trim() : '';
});

const characterCount = computed(() => normalizedText.value.length);

const wordCount = computed(() => {
  if (normalizedText.value === '') {
    return 0;
  }
  return normalizedText.value.split(/\s+/).filter((entry) => entry !== '').length;
});

const selectedVoice = computed(() => {
  const voice = getVoiceOptionById(selectedVoiceId.value);
  return voice !== undefined && voice !== null ? voice : null;
});

const playgroundVoiceType = computed(() => 'vtv');

const agentPreviewMode = computed(() => {
  if (isVtvVoiceOption(selectedVoice.value) === true) {
    return 'default';
  }
  return 'quality';
});

const textDirection = computed(() => {
  const value = normalizedText.value;
  if (/[\u0590-\u05FF]/.test(value) === true) {
    return 'rtl';
  }
  if (/[\u0600-\u06FF]/.test(value) === true) {
    return 'rtl';
  }
  const normalizedLanguage = typeof language.value === 'string' ? language.value.trim().toLowerCase() : '';
  if (normalizedLanguage === 'hebrew' || normalizedLanguage === 'he' || normalizedLanguage === 'arabic' || normalizedLanguage === 'ar') {
    return 'rtl';
  }
  return 'ltr';
});

const replacementItems = computed(() => {
  const items = [];
  for (let index = 0; index < termsPackages.value.length; index += 1) {
    const item = termsPackages.value[index];
    const packageId = item !== undefined && item !== null && typeof item.packageId === 'string' ? item.packageId : '';
    if (selectedTermsPackages.value.includes(packageId) === true) {
      const fineTuningItems = getFineTuningItems(item);
      for (let termIndex = 0; termIndex < fineTuningItems.length; termIndex += 1) {
        const entry = fineTuningItems[termIndex];
        if (entry.term !== '' && entry.spokenAs !== '') {
          items.push(entry);
        }
      }
    }
  }
  return items.sort((a, b) => b.term.length - a.term.length);
});

const speechText = computed(() => {
  return buildSpeechText(normalizedText.value);
});

const estimatedSeconds = computed(() => {
  return estimateSeconds(speechText.value);
});

const remainingSeconds = computed(() => {
  if (remainingVoiceMinutes.value === null) {
    return null;
  }
  return Math.max(0, Number(remainingVoiceMinutes.value) * 60);
});

const hasEnoughEstimatedSeconds = computed(() => {
  if (remainingSeconds.value === null) {
    return true;
  }
  if (estimatedSeconds.value <= 0) {
    return true;
  }
  return remainingSeconds.value >= estimatedSeconds.value;
});

const estimatedAfterGenerateSeconds = computed(() => {
  if (remainingSeconds.value === null) {
    return null;
  }
  return Math.max(0, remainingSeconds.value - estimatedSeconds.value);
});

const balancePercent = computed(() => {
  if (remainingSeconds.value === null) {
    return 100;
  }
  if (totalVoiceMinutes.value === null) {
    return 100;
  }
  const totalSeconds = Number(totalVoiceMinutes.value) * 60;
  if (totalSeconds <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, remainingSeconds.value / totalSeconds * 100));
});

const balanceProgressStyle = computed(() => ({
  width: `${balancePercent.value}%`
}));

const remainingSecondsLabel = computed(() => {
  if (voiceUsageLoading.value === true) {
    return t('voicePlayground.loading', 'Loading');
  }
  if (remainingSeconds.value === null) {
    return t('voicePlayground.unlimited', 'Unlimited');
  }
  return formatSeconds(remainingSeconds.value);
});

const estimatedAfterGenerateLabel = computed(() => {
  if (estimatedAfterGenerateSeconds.value === null) {
    return t('voicePlayground.unlimited', 'Unlimited');
  }
  return formatSeconds(estimatedAfterGenerateSeconds.value);
});

const generateButtonLabel = computed(() => {
  return `${t('voicePlayground.generate', 'Generate')} (${formatSeconds(estimatedSeconds.value)})`;
});

const generateHelpText = computed(() => {
  if (normalizedText.value === '') {
    return t('voicePlayground.writeHint', 'Write a sentence to estimate the paid preview.');
  }
  if (hasEnoughEstimatedSeconds.value !== true) {
    return t('voicePlayground.notEnoughSeconds', 'This preview is longer than the remaining balance. Shorten the text or purchase more voice minutes.');
  }
  return t('voicePlayground.billingHint', 'Generating will spend the estimated seconds first, then refresh with the exact billed duration.');
});

const isGenerateDisabled = computed(() => {
  if (generating.value === true) {
    return true;
  }
  if (normalizedText.value === '') {
    return true;
  }
  if (selectedVoiceId.value === '') {
    return true;
  }
  if (hasEnoughEstimatedSeconds.value !== true) {
    return true;
  }
  return false;
});

function normalizeNumber(value, fallbackValue) {
  const numberValue = Number(value);
  if (Number.isFinite(numberValue) === true) {
    return numberValue;
  }
  return fallbackValue;
}

function getAuthToken() {
  if (typeof document === 'undefined') {
    return '';
  }
  if (globalState !== undefined && globalState !== null && globalState.authUsesHttpOnlyCookies === true) {
    return '';
  }
  const readCookieValue = (name) => {
    const target = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split('; ');
    for (let index = 0; index < cookies.length; index += 1) {
      const row = cookies[index];
      if (typeof row === 'string' && row.startsWith(target) === true) {
        const rawValue = row.slice(target.length);
        try {
          return decodeURIComponent(rawValue);
        } catch (error) {
          return rawValue;
        }
      }
    }
    return '';
  };
  const authToken = readCookieValue('authToken').trim();
  if (authToken !== '') {
    return authToken;
  }
  const jwtToken = readCookieValue('jwtToken').trim();
  if (jwtToken !== '') {
    return jwtToken;
  }
  return '';
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getPreviewPayload(text) {
  return {
    voiceId: selectedVoiceId.value,
    text: text,
    languageCode: detectLanguageCode(text),
    speed: normalizeNumber(speed.value, 1),
    previewSource: 'voicePlayground',
    metadata: {
      selectedTermsPackages: selectedTermsPackages.value,
      selectedTerms: selectedTerms.value,
      accent: accent.value,
      tone: tone.value,
      agentPreviewMode: agentPreviewMode.value,
      voiceType: playgroundVoiceType.value,
      mode: mode.value,
      cancellingMode: cancellingMode.value,
      customPacingEnabled: customPacingEnabled.value,
      pacing: pacing.value,
      durationStretch: durationStretch.value,
      useDashPauses: useDashPauses.value,
      pauseEveryWords: pauseEveryWords.value,
      dashPauseMs: dashPauseMs.value,
      commaPauseMs: commaPauseMs.value,
      periodPauseMs: periodPauseMs.value,
      estimatedSeconds: estimatedSeconds.value,
      voiceName: selectedVoice.value !== null ? resolveVoiceDisplayName(selectedVoice.value, language.value, accent.value) : ''
    }
  };
}

async function generateBatchPreview(text) {
  const response = await axiosInstance.post(
    '/api/voicePlayground/preview',
    getPreviewPayload(text),
    { responseType: 'blob' }
  );
  const audioBlob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'audio/wav' });
  const objectUrl = window.URL.createObjectURL(audioBlob);
  const audio = new Audio(objectUrl);
  const headerSeconds = Number(response.headers['x-voice-preview-seconds']);
  const billedSeconds = Number.isFinite(headerSeconds) === true && headerSeconds > 0 ? headerSeconds : estimatedSeconds.value;
  audioUrl.value = objectUrl;
  audioElement.value = audio;
  lastGeneratedSeconds.value = billedSeconds;
  applyLocalBilling(billedSeconds);
  audio.onended = () => {};
  audio.onerror = () => {};
  try {
    await audio.play();
  } catch (playError) {}
  await loadVoiceUsage();
  return billedSeconds;
}

function getFineTuningItems(item) {
  const items = [];
  if (item !== undefined && item !== null && Array.isArray(item.fineTuningItems) === true) {
    for (let index = 0; index < item.fineTuningItems.length; index += 1) {
      pushFineTuningItem(items, item.fineTuningItems[index]);
    }
  }
  if (items.length === 0 && item !== undefined && item !== null && Array.isArray(item.items) === true) {
    for (let index = 0; index < item.items.length; index += 1) {
      const entry = item.items[index];
      const entityType = entry !== undefined && entry !== null && typeof entry.entityType === 'string' ? entry.entityType.trim() : '';
      const spokenAs = entry !== undefined && entry !== null && typeof entry.spokenAs === 'string' ? entry.spokenAs.trim() : '';
      const shouldUseItem = entityType === 'fineTuning' ? true : spokenAs !== '';
      if (shouldUseItem === true) {
        pushFineTuningItem(items, entry);
      }
    }
  }
  return items;
}

function pushFineTuningItem(items, item) {
  if (item === undefined) {
    return;
  }
  if (item === null) {
    return;
  }
  const term = typeof item.term === 'string' ? item.term.trim() : '';
  const spokenAs = typeof item.spokenAs === 'string' ? item.spokenAs.trim() : '';
  if (term === '') {
    return;
  }
  if (spokenAs === '') {
    return;
  }
  const existingIndex = items.findIndex((entry) => entry.term === term && entry.spokenAs === spokenAs);
  if (existingIndex === -1) {
    items.push({
      term: term,
      spokenAs: spokenAs
    });
  }
}

function applyReplacements(value) {
  let result = value;
  for (let index = 0; index < replacementItems.value.length; index += 1) {
    const entry = replacementItems.value[index];
    if (entry.term !== '' && entry.spokenAs !== '' && result.indexOf(entry.term) >= 0) {
      result = result.split(entry.term).join(entry.spokenAs);
    }
  }
  return result;
}

function insertWordPauses(value) {
  const pauseCount = Math.floor(normalizeNumber(pauseEveryWords.value, 0));
  if (customPacingEnabled.value !== true) {
    return value;
  }
  if (useDashPauses.value !== true) {
    return value;
  }
  if (pauseCount <= 0) {
    return value;
  }
  const words = value.split(/\s+/).filter((entry) => entry !== '');
  if (words.length <= pauseCount) {
    return value;
  }
  const output = [];
  for (let index = 0; index < words.length; index += 1) {
    output.push(words[index]);
    if ((index + 1) % pauseCount === 0 && index < words.length - 1) {
      output.push('-');
    }
  }
  return output.join(' ');
}

function buildSpeechText(value) {
  if (value === '') {
    return '';
  }
  return insertWordPauses(applyReplacements(value));
}

function countCharacters(value, characters) {
  let count = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (characters.indexOf(value.charAt(index)) >= 0) {
      count += 1;
    }
  }
  return count;
}

function estimateSeconds(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') {
    return 0;
  }
  const words = text.split(/\s+/).filter((entry) => entry !== '').length;
  const charsPerSecond = textDirection.value === 'rtl' ? 10 : 13;
  const secondsFromChars = text.length / charsPerSecond;
  const secondsFromWords = words > 0 ? words / 2.5 : 0;
  const speedValue = Math.max(0.5, normalizeNumber(speed.value, 1));
  let seconds = Math.max(1.1, secondsFromChars, secondsFromWords) / speedValue;
  if (customPacingEnabled.value === true) {
    seconds = seconds * Math.max(0.5, normalizeNumber(pacing.value, 1.05));
    seconds = seconds * Math.max(0.8, normalizeNumber(durationStretch.value, 1));
    const dashCount = useDashPauses.value === true ? countCharacters(text, '-') : 0;
    const commaCount = countCharacters(text, ',');
    const periodCount = countCharacters(text, '.!?');
    const wordPauseCount = normalizeNumber(pauseEveryWords.value, 0) > 0 ? Math.floor(words / Math.max(1, normalizeNumber(pauseEveryWords.value, 0))) : 0;
    seconds += dashCount * Math.max(0, normalizeNumber(dashPauseMs.value, 0)) / 1000;
    seconds += commaCount * Math.max(0, normalizeNumber(commaPauseMs.value, 0)) / 1000;
    seconds += periodCount * Math.max(0, normalizeNumber(periodPauseMs.value, 0)) / 1000;
    seconds += wordPauseCount * Math.max(0, normalizeNumber(dashPauseMs.value, 0)) / 1000;
  }
  return Math.max(1.1, seconds);
}

function formatSeconds(value) {
  const secondsValue = Number.isFinite(Number(value)) === true ? Number(value) : 0;
  if (secondsValue <= 0) {
    return '0.0s';
  }
  if (secondsValue >= 60) {
    const minutes = Math.floor(secondsValue / 60);
    const seconds = Math.round(secondsValue - minutes * 60);
    return `${minutes}m ${seconds}s`;
  }
  if (secondsValue >= 10) {
    return `${secondsValue.toFixed(0)}s`;
  }
  return `${secondsValue.toFixed(1)}s`;
}

function resolvePlaygroundLanguageCode(value) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (normalized === '') {
    return 'en';
  }
  for (let index = 0; index < geminiLanguageDetails.length; index += 1) {
    const entry = geminiLanguageDetails[index];
    if (entry !== undefined && entry !== null) {
      const entryCode = typeof entry.code === 'string' ? entry.code.trim().toLowerCase() : '';
      const entryName = typeof entry.name === 'string' ? entry.name.trim().toLowerCase() : '';
      if (entryCode === normalized || entryName === normalized) {
        if (entryCode === 'iw') {
          return 'he';
        }
        return entryCode !== '' ? entryCode : 'en';
      }
    }
  }
  return normalized;
}

function detectLanguageCode(value) {
  if (/[\u0590-\u05FF]/.test(value) === true) {
    return 'he';
  }
  if (/[\u0600-\u06FF]/.test(value) === true) {
    return 'ar';
  }
  if (language.value !== '' && language.value !== 'auto') {
    return resolvePlaygroundLanguageCode(language.value);
  }
  return 'en';
}

function clearAudio() {
  if (audioElement.value !== null) {
    try {
      audioElement.value.pause();
    } catch (error) {}
    audioElement.value.onended = null;
    audioElement.value.onerror = null;
    audioElement.value = null;
  }
  if (audioUrl.value !== '') {
    window.URL.revokeObjectURL(audioUrl.value);
    audioUrl.value = '';
  }
}

function resetPlayground() {
  playgroundText.value = '';
  selectedTerms.value = [];
  lastGeneratedSeconds.value = 0;
  clearAudio();
}

function setExampleText(value) {
  playgroundText.value = value;
}

function getVoiceRuntimeType(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  return typeof voice.runtimeType === 'string' ? voice.runtimeType.trim().toLowerCase() : '';
}

function isDefaultRealtimeVoiceOption(voiceParam) {
  if (getVoiceRuntimeType(voiceParam) === 'default') {
    return true;
  }
  return false;
}

function ensureSelectedPlaygroundVoice() {
  const selectedId = typeof selectedVoiceId.value === 'string' ? selectedVoiceId.value.trim() : '';
  for (let index = 0; index < voiceOptions.length; index += 1) {
    const voice = voiceOptions[index];
    if (voice !== undefined && voice !== null && voice.voiceId === selectedId && voice.hidden !== true && isDefaultRealtimeVoiceOption(voice) === true) {
      return;
    }
  }
  for (let index = 0; index < voiceOptions.length; index += 1) {
    const voice = voiceOptions[index];
    if (voice !== undefined && voice !== null && voice.hidden !== true && isDefaultRealtimeVoiceOption(voice) === true && typeof voice.voiceId === 'string' && voice.voiceId.trim() !== '') {
      selectedVoiceId.value = voice.voiceId;
      return;
    }
  }
  selectedVoiceId.value = defaultVoiceId;
}

async function loadTermsPackages() {
  try {
    const response = await axiosInstance.get('/api/termsPackages', { headers: getAuthHeaders() });
    termsPackages.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    termsPackages.value = [];
  }
}

async function loadVoiceUsage() {
  voiceUsageLoading.value = true;
  try {
    const response = await axiosInstance.get('/api/termsPackages/voiceUsage', { headers: getAuthHeaders() });
    const data = response.data !== undefined && response.data !== null ? response.data : {};
    const voiceMinutes = data.voiceMinutes !== undefined && data.voiceMinutes !== null ? data.voiceMinutes : null;
    if (voiceMinutes !== null && Number.isFinite(Number(voiceMinutes.remaining)) === true) {
      remainingVoiceMinutes.value = Number(voiceMinutes.remaining);
    } else {
      remainingVoiceMinutes.value = null;
    }
    if (voiceMinutes !== null && Number.isFinite(Number(voiceMinutes.totalCredits)) === true) {
      totalVoiceMinutes.value = Number(voiceMinutes.totalCredits);
    } else {
      totalVoiceMinutes.value = null;
    }
  } catch (error) {
    remainingVoiceMinutes.value = null;
    totalVoiceMinutes.value = null;
  } finally {
    voiceUsageLoading.value = false;
  }
}

function applyLocalBilling(seconds) {
  const billedSeconds = Number.isFinite(Number(seconds)) === true ? Number(seconds) : 0;
  if (billedSeconds <= 0) {
    return;
  }
  if (remainingVoiceMinutes.value !== null) {
    remainingVoiceMinutes.value = Math.max(0, remainingVoiceMinutes.value - billedSeconds / 60);
  }
}

async function resolvePreviewErrorMessage(error) {
  const fallback = t('voicePlayground.generateFailed', 'Failed to generate voice preview.');
  const responseData = error !== undefined && error !== null && error.response !== undefined && error.response !== null ? error.response.data : null;
  if (typeof Blob !== 'undefined' && responseData instanceof Blob) {
    try {
      const blobText = await responseData.text();
      if (blobText.trim() !== '') {
        try {
          const parsed = JSON.parse(blobText);
          const parsedMessage = typeof parsed.message === 'string' ? parsed.message.trim() : '';
          if (parsedMessage !== '') {
            return parsedMessage;
          }
        } catch (parseError) {
          return blobText.trim();
        }
      }
    } catch (blobError) {}
  }
  const responseMessage = responseData !== null && typeof responseData.message === 'string' ? responseData.message.trim() : '';
  if (responseMessage !== '') {
    return responseMessage;
  }
  const errorMessage = error !== undefined && error !== null && typeof error.message === 'string' ? error.message.trim() : '';
  if (errorMessage !== '') {
    return errorMessage;
  }
  return fallback;
}

async function generateVoice() {
  if (isGenerateDisabled.value === true) {
    if (hasEnoughEstimatedSeconds.value !== true) {
      console.error('Voice playground: preview longer than remaining voice balance');
    }
    return;
  }
  const text = speechText.value;
  if (text === '') {
    console.error('Voice playground: preview text is empty');
    return;
  }
  if (selectedVoiceId.value === '') {
    console.error('Voice playground: no voice selected');
    return;
  }
  generating.value = true;
  clearAudio();
  try {
    await generateBatchPreview(text);
    toast.success(t('voicePlayground.generated', 'Voice generated and seconds were deducted.'));
  } catch (error) {
    clearAudio();
    console.error('Voice playground preview failed', error);
    toast.error(await resolvePreviewErrorMessage(error));
  } finally {
    generating.value = false;
  }
}

onMounted(async () => {
  try {
    await fetchVoices();
  } catch (error) {}
  ensureSelectedPlaygroundVoice();
  await Promise.all([loadTermsPackages(), loadVoiceUsage()]);
});

watch(() => voiceOptions.length, () => {
  ensureSelectedPlaygroundVoice();
});

watch(() => selectedTermsPackages.value, () => {
  if (termsPackages.value.length === 0) {
    loadTermsPackages();
  }
});

onBeforeUnmount(() => {
  clearAudio();
});
</script>

<style scoped>
.voice-playground-textarea {
  font-size: 1rem;
  line-height: 1.75;
}

.voice-playground-selectors :deep(.input),
.voice-playground-selectors :deep(.select),
.voice-playground-selectors :deep(textarea),
.voice-playground-selectors :deep(.custom-selector-container > .flex > button),
.voice-playground-selectors :deep(.custom-selector-container > div > button),
.voice-playground-selectors :deep(.custom-selector-container button.btn),
.voice-playground-selectors :deep(.custom-selector-container button[type='button']),
.voice-playground-selectors :deep(button.btn) {
  background-color: #ffffff;
}

.voice-playground-selectors :deep(.custom-selector-container .input),
.voice-playground-selectors :deep(.voice-advanced-card),
.voice-playground-selectors :deep(.voice-advanced-body .input) {
  background-color: #ffffff;
}

:global(.dark) .voice-playground-selectors :deep(.input),
:global(.dark) .voice-playground-selectors :deep(.select),
:global(.dark) .voice-playground-selectors :deep(textarea),
:global(.dark) .voice-playground-selectors :deep(.custom-selector-container > .flex > button),
:global(.dark) .voice-playground-selectors :deep(.custom-selector-container > div > button),
:global(.dark) .voice-playground-selectors :deep(.custom-selector-container button.btn),
:global(.dark) .voice-playground-selectors :deep(.custom-selector-container button[type='button']),
:global(.dark) .voice-playground-selectors :deep(button.btn),
:global(.dark) .voice-playground-selectors :deep(.custom-selector-container .input),
:global(.dark) .voice-playground-selectors :deep(.voice-advanced-card),
:global(.dark) .voice-playground-selectors :deep(.voice-advanced-body .input) {
  background-color: rgb(31 41 55);
}

.voice-example-buttons .btn {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  border-color: hsl(var(--p) / 0.3);
  background: hsl(var(--b1));
  color: hsl(var(--p));
  font-weight: 400;
  text-transform: none;
}

.voice-example-buttons .btn:hover {
  border-color: hsl(var(--p));
  background: hsl(var(--p));
  color: hsl(var(--pc));
}

.voice-playground-actions {
  margin-left: -0.35rem;
  margin-right: -0.35rem;
}

.voice-playground-actions .btn {
  margin-left: 0.35rem;
  margin-right: 0.35rem;
  margin-bottom: 0.75rem;
  min-width: 8.75rem;
}

.voice-playground-settings-panel :deep(.form-control) {
  margin-bottom: 0.7rem;
}

.voice-playground-settings-panel :deep(.form-control.mt-4) {
  margin-top: 0.85rem;
}

.voice-playground-settings-panel :deep(.label) {
  padding-top: 0.2rem;
  padding-bottom: 0.25rem;
}

.voice-playground-settings-panel :deep(.label-text) {
  font-size: 0.875rem;
}

.voice-playground-settings-panel :deep(.input),
.voice-playground-settings-panel :deep(.select) {
  min-height: 2.35rem;
  font-size: 0.875rem;
}

.voice-audio-el {
  min-height: 2.5rem;
}

@media (max-width: 1279px) {
  .voice-playground-columns {
    overflow: visible;
  }
}
</style>
